import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Phone, Building2, Trash2, RefreshCw, Search } from "lucide-react";

const STATUS = [
  { v: "new", l: "Nouveau", cls: "bg-brand-yellow/20 text-brand-yellow border-brand-yellow/30" },
  { v: "contacted", l: "Contacté", cls: "bg-blue-500/20 text-blue-300 border-blue-400/30" },
  { v: "negotiating", l: "En négo", cls: "bg-purple-500/20 text-purple-300 border-purple-400/30" },
  { v: "won", l: "Gagné", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30" },
  { v: "lost", l: "Perdu", cls: "bg-white/10 text-white/50 border-white/20" },
  { v: "spam", l: "Spam", cls: "bg-brand-red/20 text-brand-red border-brand-red/30" },
];

export const AdminLeadsPanel = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/leads").then(({ data }) => setLeads(data || []))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter && l.status !== filter) return false;
      if (!s) return true;
      return [l.company, l.contact_name, l.email, l.phone, l.message].some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [leads, q, filter]);

  const counts = useMemo(() => {
    const c = { total: leads.length };
    STATUS.forEach((s) => { c[s.v] = leads.filter((l) => l.status === s.v).length; });
    return c;
  }, [leads]);

  const updateStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.lead_id}`, { status });
      setLeads((prev) => prev.map((l) => l.lead_id === lead.lead_id ? { ...l, status } : l));
    } catch { toast.error("Erreur mise à jour"); }
  };
  const remove = async (lead) => {
    if (!window.confirm(`Supprimer la demande de "${lead.company}" ?`)) return;
    try {
      await api.delete(`/admin/leads/${lead.lead_id}`);
      setLeads((prev) => prev.filter((l) => l.lead_id !== lead.lead_id));
      toast.success("Supprimé");
    } catch { toast.error("Erreur"); }
  };

  const fmt = (d) => { try { return new Date(d.replace(" ", "T") + "Z").toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return d; } };

  return (
    <div className="space-y-5" data-testid="admin-leads-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-black">Demandes annonceurs</h2>
          <p className="text-xs text-white/50">Leads collectés via le formulaire <code className="text-brand-orange">/annoncer</code></p>
        </div>
        <button onClick={load} className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="leads-refresh"><RefreshCw size={12} /> Recharger</button>
      </div>

      {/* Stats counters */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
        <Counter label="Total" value={counts.total} active={!filter} onClick={() => setFilter("")} testid="leads-count-all" />
        {STATUS.map((s) => (
          <Counter key={s.v} label={s.l} value={counts[s.v] || 0} cls={s.cls} active={filter === s.v} onClick={() => setFilter(filter === s.v ? "" : s.v)} testid={`leads-count-${s.v}`} />
        ))}
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (entreprise, email, message…)"
          className="w-full rounded-xl bg-white/[0.04] border border-white/10 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-brand-orange/60"
          data-testid="leads-search" />
      </div>

      {loading && <p className="text-white/50 text-sm">Chargement…</p>}
      {!loading && filtered.length === 0 && <p className="text-white/50 text-sm">Aucun lead {filter ? `avec le statut "${filter}"` : "pour l'instant"}.</p>}

      <div className="space-y-3">
        {filtered.map((l) => {
          const stCfg = STATUS.find((s) => s.v === l.status) || STATUS[0];
          return (
            <div key={l.lead_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={`lead-row-${l.lead_id}`}>
              <div className="flex items-start gap-3 mb-2 flex-wrap">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center"><Building2 size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold">{l.company}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest font-bold ${stCfg.cls}`}>{stCfg.l}</span>
                    {l.pack_chosen && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-orange/20 text-brand-orange border border-brand-orange/30 uppercase font-bold">{l.pack_chosen}</span>}
                  </div>
                  <p className="text-sm text-white/70">{l.contact_name}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-white/60 flex-wrap">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-brand-orange"><Mail size={11} /> {l.email}</a>
                    {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-brand-orange"><Phone size={11} /> {l.phone}</a>}
                    {l.budget && <span>· budget {l.budget}</span>}
                    {l.category_interest && <span>· {l.category_interest}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[11px] text-white/40">{fmt(l.created_at)}</p>
                </div>
              </div>
              {l.message && (
                <pre className="text-xs text-white/80 bg-black/30 rounded-lg p-3 mt-2 whitespace-pre-wrap font-sans" data-testid={`lead-message-${l.lead_id}`}>{l.message}</pre>
              )}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <select value={l.status} onChange={(e) => updateStatus(l, e.target.value)}
                  className="rounded-lg bg-white/[0.04] border border-white/10 px-2 py-1.5 text-xs focus:outline-none focus:border-brand-orange/60"
                  data-testid={`lead-status-${l.lead_id}`}>
                  {STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
                <a href={`mailto:${l.email}?subject=Voamada%20Ads%20%E2%80%94%20${encodeURIComponent(l.company)}`}
                  className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`lead-reply-${l.lead_id}`}>
                  <Mail size={12} /> Répondre
                </a>
                <button onClick={() => remove(l)} className="text-white/40 hover:text-brand-red p-1.5 ml-auto" data-testid={`lead-delete-${l.lead_id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Counter = ({ label, value, cls = "bg-white/5 text-white border-white/10", active, onClick, testid }) => (
  <button onClick={onClick} className={`rounded-xl border px-3 py-2 text-left transition ${cls} ${active ? "ring-2 ring-brand-orange" : "hover:brightness-110"}`} data-testid={testid}>
    <div className="text-2xl font-display font-black leading-none">{value}</div>
    <div className="text-[10px] uppercase tracking-widest mt-1 opacity-80">{label}</div>
  </button>
);

export default AdminLeadsPanel;
